import React, { forwardRef, useEffect, useRef } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/utils/cn';
import { tokens } from '@/theme/tokens';
import type { CheckboxProps } from './Checkbox.types';

/**
 * # Checkbox
 *
 * Zaskrtavaci policko s podporou indeterminate stavu.
 *
 * @example
 * <Checkbox label="Souhlasim s podminkami" />
 * <Checkbox label="Vybrat vse" indeterminate />
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      error,
      indeterminate,
      className,
      id,
      disabled,
      onChange,
      checked,
      'data-testid': testId,
      ...nativeProps
    },
    ref,
  ) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!error;
    const innerRef = useRef<HTMLInputElement>(null);

    // Combine refs
    useEffect(() => {
      if (!innerRef.current) return;

      if (typeof ref === 'function') {
        ref(innerRef.current);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = innerRef.current;
      }
    }, [ref]);

    // Handle indeterminate state
    useEffect(() => {
      if (innerRef.current) {
        innerRef.current.indeterminate = !!indeterminate;
      }
    }, [indeterminate]);

    return (
      <div className={cn('flex items-start gap-3', className)}>
        <div className="relative flex items-center pt-0.5">
          <input
            type="checkbox"
            ref={innerRef}
            id={checkboxId}
            disabled={disabled}
            checked={checked}
            onChange={onChange}
            data-testid={testId}
            {...(hasError ? { 'aria-invalid': true } : {})}
            className="peer sr-only" // Visually hidden
            {...nativeProps}
          />

          {/* Custom Checkbox visual */}
          <div
            className={cn(
              'h-5 w-5 shrink-0 rounded border transition-all duration-200',
              'flex items-center justify-center',
              tokens.radius.sm,
              // Default state
              'border-gray-300 bg-white dark:border-zinc-600 dark:bg-zinc-800',
              // Focus ring (via peer-focus on hidden input)
              'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500/20 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-zinc-900',
              // Checked / Indeterminate state
              (checked || indeterminate) &&
                'border-primary-600 bg-primary-600 text-white dark:border-primary-500 dark:bg-primary-500',
              // Error state
              hasError && 'border-red-500 dark:border-red-400',
              // Disabled
              disabled && 'cursor-not-allowed opacity-50 bg-gray-100 dark:bg-zinc-800',
            )}
          >
            {/* Icons */}
            <span
              className={cn(
                'absolute inset-0 flex items-center justify-center transition-all duration-200 pointer-events-none text-white',
                checked || indeterminate ? 'opacity-100 scale-100' : 'opacity-0 scale-50',
              )}
            >
              {indeterminate ? (
                <Minus size={14} strokeWidth={3} />
              ) : (
                <Check size={14} strokeWidth={3} />
              )}
            </span>
          </div>
        </div>

        {/* Text content */}
        {(label || description || error) && (
          <div className="flex flex-col">
            {label && (
              <label
                htmlFor={checkboxId}
                className={cn(
                  'text-sm font-medium text-gray-900 dark:text-zinc-100 cursor-pointer select-none',
                  disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-sm text-gray-500 dark:text-zinc-400">{description}</p>
            )}
            {error && (
              <p className="text-sm text-red-500 dark:text-red-400 mt-1" role="alert">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';
